import VideoAttribution, { videoPath } from "@/components/VideoAttribution";
import MediaImage from "@/components/MediaImage";
import { isLiveCreator } from "@/components/LiveDemoBadge";
import { isDemoMedia } from "@/lib/media";
import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatError } from "@/lib/api";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import * as VisuallyHidden from "@radix-ui/react-visually-hidden";
import {
  Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList,
} from "@/components/ui/command";
import { formatNumber } from "@/lib/format";
import { focusIfShown } from "@/lib/focus";
import PlatformBadge from "@/components/PlatformBadge";

const MIN_QUERY = 2;
const NO_RESULTS = { creators: [], videos: [] };
const IDLE = { status: "idle", query: "", results: NO_RESULTS };
const validResults = (data) => data && Array.isArray(data.creators) && Array.isArray(data.videos);

// `restoreFocusFallback`: called when the element focused before opening is gone or hidden at close time.
export default function GlobalSearch({ open, onOpenChange, restoreFocusFallback }) {
  const [q, setQ] = useState("");
  // status: idle (nothing to search) | waiting (debounce) | loading | success | error, always for `query`.
  const [search, setSearch] = useState(IDLE);
  // Every input change, close or new request bumps the sequence; only the latest request may publish.
  const sequence = useRef(0);
  // The dialog is opened without a Radix trigger (header button, Ctrl+K): remember where focus was to return it there.
  const returnFocus = useRef(null);
  const nav = useNavigate();

  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        onOpenChange(true);
      }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onOpenChange]);

  const run = useCallback(async (term) => {
    const request = ++sequence.current;
    setSearch({ status: "loading", query: term, results: NO_RESULTS });
    try {
      const { data } = await api.get(`/search?q=${encodeURIComponent(term)}`);
      if (!validResults(data)) throw new Error("Réponse de recherche inutilisable.");
      if (request === sequence.current) setSearch({ status: "success", query: term, results: data });
    } catch (error) {
      if (request === sequence.current) setSearch({ status: "error", query: term, results: NO_RESULTS, error });
    }
  }, []);

  // Closing the dialog discards the query; reopening starts from an empty search.
  useEffect(() => { if (!open) setQ(""); }, [open]);

  useEffect(() => {
    const term = q.trim();
    sequence.current += 1;
    if (!open || term.length < MIN_QUERY) { setSearch(IDLE); return undefined; }
    setSearch({ status: "waiting", query: term, results: NO_RESULTS });
    const t = setTimeout(() => run(term), 200);
    return () => clearTimeout(t);
  }, [q, open, run]);

  const go = (path) => {
    onOpenChange(false);
    setQ("");
    nav(path);
  };

  const term = q.trim();
  const current = search.query === term ? search : IDLE;
  const results = current.results;
  const pending = current.status === "waiting" || current.status === "loading";
  const hasAny = results.creators.length + results.videos.length > 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="overflow-hidden p-0 max-w-xl"
        onOpenAutoFocus={() => { returnFocus.current = document.activeElement; }}
        onCloseAutoFocus={(event) => {
          const target = returnFocus.current;
          returnFocus.current = null;
          event.preventDefault();
          // The remembered element may have been removed or hidden meanwhile (a mobile drawer link after a
          // navigation or a switch to desktop width): then let the caller pick a destination still shown.
          if (target && target !== document.body && focusIfShown(target)) return;
          restoreFocusFallback?.();
        }}
      >
        <VisuallyHidden.Root>
          <DialogTitle>Global search</DialogTitle>
          <DialogDescription>Rechercher des créateurs, vidéos et textes sources non vérifiés.</DialogDescription>
        </VisuallyHidden.Root>
        <Command
          shouldFilter={false}
          className="[&_[cmdk-group-heading]]:px-2 [&_[cmdk-group-heading]]:font-medium [&_[cmdk-group-heading]]:text-muted-foreground [&_[cmdk-group]:not([hidden])_~[cmdk-group]]:pt-0 [&_[cmdk-group]]:px-2 [&_[cmdk-input-wrapper]_svg]:h-5 [&_[cmdk-input-wrapper]_svg]:w-5 [&_[cmdk-input]]:h-12 [&_[cmdk-item]]:px-2 [&_[cmdk-item]]:py-3 [&_[cmdk-item]_svg]:h-5 [&_[cmdk-item]_svg]:w-5"
        >
          <CommandInput
            placeholder="Créateurs, titres ou textes sources…"
            value={q}
            onValueChange={setQ}
            data-testid="global-search-input"
          />
          <CommandList>
            {term.length > 0 && term.length < MIN_QUERY && <div role="status" className="py-6 text-center text-sm text-muted-foreground" data-testid="global-search-hint">Saisissez au moins {MIN_QUERY} caractères.</div>}
            {pending && <div role="status" className="py-6 text-center text-sm text-muted-foreground" data-testid="global-search-loading">Recherche en cours…</div>}
            {current.status === "error" && (
              <>
                <div role="alert" className="px-4 pt-6 pb-2 text-center text-sm text-destructive" data-testid="global-search-error">La recherche a échoué : {formatError(current.error)}</div>
                <CommandGroup>
                  <CommandItem value="retry-search" onSelect={() => run(current.query)} data-testid="global-search-retry" className="justify-center">Réessayer</CommandItem>
                </CommandGroup>
              </>
            )}
            {current.status === "success" && !hasAny && <CommandEmpty data-testid="global-search-empty">Aucun résultat pour « {current.query} ».</CommandEmpty>}
            {results.creators.length > 0 && (
              <CommandGroup heading="Creators">
                {results.creators.map((c) => (
                  <CommandItem
                    key={c.id}
                    value={`creator-${c.id}`}
                    onSelect={() => go(`/creators/${c.id}`)}
                    data-testid={`search-creator-${c.id}`}
                    className="flex items-center gap-3"
                  >
                    <MediaImage src={c.avatar} alt="" name={c.name} kind="avatar" demo={!isLiveCreator(c)} className="h-7 w-7 rounded-full object-cover" />
                    <div className="flex-1">
                      <div className="text-sm font-medium">{c.name}</div>
                      <div className="text-xs text-muted-foreground">{c.handle}</div>
                    </div>
                    <PlatformBadge platform={c.platform} />
                  </CommandItem>
                ))}
              </CommandGroup>
            )}
            {results.videos.length > 0 && (
              <CommandGroup heading="Videos">
                {results.videos.map((v) => (
                  <CommandItem
                    key={v.id}
                    value={`video-${v.id}`}
                    onSelect={() => go(videoPath(v))}
                    data-testid={`search-video-${v.id}`}
                    className="flex items-center gap-3"
                  >
                    <MediaImage src={v.thumbnail} alt="" demo={isDemoMedia(v)} showLabel={false} className="h-10 w-14 rounded object-cover" />
                    <div className="flex-1 min-w-0">
                      <div className="text-sm truncate">{v.title}</div>
                      <div className="text-xs text-muted-foreground">
                        {v.creator_handle} • {formatNumber(v.views)} views
                        <VideoAttribution video={v} />
                      </div>
                    </div>
                    <PlatformBadge platform={v.platform} />
                  </CommandItem>
                ))}
              </CommandGroup>
            )}
          </CommandList>
        </Command>
      </DialogContent>
    </Dialog>
  );
}
